import { z } from 'zod';
import { baseSchema } from '../baseSchemas';
import { warehouseSchema } from '../materials/getWarehouses';
import { paymentTypeSchema } from './getPaymentTypes';
import { traderSiteSchema } from '../traders/getTraderSites';
import { expensesAndChargesSchema } from './getExpensesAndCharges';
import { alterCode } from '../alterCodes/getAlterCodesWithConditions';
import { vatFactorSchema } from '../vat/vatFactorSchema';

export const documentDetailsDiscountsSchema = z.object({
  id: z.string(),
  documentDetailId: z.string(),
  sequence: z.number(),
  discountOrigin: z.number(),
  discountTotalValue: z.number(),
  discountUnitValue: z.number(),
  discountPercentage: z.number(),
});

export const documentDetailSchema = z.object({
  id: z.string(),
  isFromManager: z.boolean(),
  documentHeaderId: z.string(),
  warehouseId: z.string().optional(),
  warehouse: warehouseSchema.optional(),
  materialId: z.string(),
  materialName: z.string(),
  materialCode: z.string(),
  material: z.object({
    allowManualPrice: z.boolean().optional(),
    allowZeroValue: z.boolean().optional(),
    image: z.string().optional(),
  }),
  materialAlterCodeId: z.string(),
  materialAlterCode: z.object({
    id: z.string(),
    materialId: z.string(),
    alterCode: alterCode,
  }),
  quantity: z.number(),
  quantity2: z.number().optional(),
  quantity3: z.number().optional(),
  totalQuantity: z.number().optional(),
  measurementUnit1Id: z.string(),
  measurementUnit2Id: z.string().optional(),
  measurementUnit3Id: z.string().optional(),
  relationFactor1: z.number().optional(),
  relationFactor2: z.number().optional(),
  relationFactor3: z.number().optional(),
  unitPrice: z.number(),
  netTotal: z.number(),
  vatFactorValue: z.number().optional(),
  vatFactorId: z.string(),
  vatFactor: vatFactorSchema,
  lineVatTotal: z.number(),
  grossTotal: z.number(),
  appliedDiscounts: z.string().optional(),
  totalBeforeDiscount: z.number(),
  unitDiscount: z.number(),
  totalDiscount: z.number(),
  percentageDiscount: z.number(),
  documentDetailDiscounts: z.array(documentDetailsDiscountsSchema).optional(),
  allowDiscount: z.boolean().optional(),
  exists: z.boolean().optional(),
  notExists: z.boolean().optional(),
});

export const documentDetailsSchema = z.array(documentDetailSchema);

export const documentPaymentSchema = z.object({
  id: z.string(),
  paymentType: paymentTypeSchema,
  paymentTypeId: z.string(),
  startDate: z.string(),
  endDate: z.string(),
});

export const documentHeaderExpensesAndChargesSchema = z.object({
  id: z.string(),
  isActive: z.boolean(),
  isFromManager: z.boolean(),
  grossValue: z.number(),
  vatFactorValue: z.number().optional(),
  vatValue: z.number().optional(),
  value: z.number(),
  expensesAndChargesId: z.string(),
  expensesAndCharges: expensesAndChargesSchema,
  extraDescription: z.string().optional(),
});

export const documentHeaderDiscount = z.object({
  id: z.string(),
  documentHeaderId: z.string(),
  discountOrigin: z.number(),
  sequence: z.number().optional(),
  value: z.number(),
  onGrossValue: z.boolean(),
});

export const documentHeaderSchema = z.object({
  id: z.string(),
  isFromManager: z.boolean(),
  createUserId: z.string(),
  companyId: z.string().optional(),
  company: z.object({ id: z.string(), code: z.string(), name: z.string() }),
  companySiteId: z.string(),
  documentTypeId: z.string().optional(),
  documentType: baseSchema.extend({
    allowZeroValues: z.boolean().optional(),
    allowZeroQty: z.boolean().optional(),
    allowManualPrices: z.boolean().optional(),
    allowEditClosedDocuments: z.boolean().optional(),
    allowHeaderDiscounts: z.boolean().optional(),
    requireWarehouse: z.boolean().optional(),
    requireDeliveryType: z.boolean().optional(),
    requirePaymentType: z.boolean().optional(),
    maxDetailQty: z.number().optional(),
    maxDetailUnitPrice: z.number().optional(),
    defaultReportingTemplateId: z.string().optional(),
    totalMeasurementUnits: z.number().optional(),
  }),
  documentSeriesId: z.string().optional(),
  documentSeries: baseSchema,
  divisionId: z.string().optional(),
  division: baseSchema,
  documentNumber: z.number().optional(),
  documentStatusId: z.string().optional(),
  documentStatus: z.object({ id: z.string(), code: z.string(), name: z.string() }),
  officialDate: z.string().optional(),
  deliveryDate: z.string().optional(),
  deliveryAddress: z.string().optional(),
  printedDate: z.string().optional(),
  approvesDate: z.string().optional(),
  remarks: z.string().optional(),
  isCanceled: z.boolean(),
  reason: z.string().optional(),
  headerRemarks: z.string().optional(),
  footerRemarks: z.string().optional(),
  notes: z.string().optional(),
  canceledById: z.string().optional(),
  issuedOnDeviceId: z.string().optional(),
  qrBarCode: z.string().optional(),
  signature: z.string().optional(),
  totalGross: z.number(),
  totalVat: z.number(),
  totalCharges: z.number().optional(),
  totalDiscounts: z.number(),
  totalNetValue: z.number(),
  totalQty: z.number(),
  latitude: z.string().optional(),
  longitude: z.string().optional(),
  warehouse: warehouseSchema.optional(),
  warehouseId: z.string().optional(),
  mediator: z
    .object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
    })
    .optional(),
  mediatorId: z.string().optional(),
  documentDetails: documentDetailsSchema.optional(),
  documentPayment: documentPaymentSchema.optional(),
  billingAddress: traderSiteSchema.optional(),
  billingAddressId: z.string().optional(),
  documentHeaderExpensesAndCharges: z.array(documentHeaderExpensesAndChargesSchema).optional(),
  documentHeaderDiscounts: z.array(documentHeaderDiscount).optional(),
  traderId: z.string().optional(),
  traderSiteId: z.string(),
  traderSite: z
    .object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
      address: z.string().optional(),
      boid: z.string().optional(),
    })
    .optional(),
  myDataSyncSuccess: z.boolean().optional(),
  myDataErrorMessage: z.string().optional(),
  myDataMark: z.number().nullish(),
  myDataUuid: z.string().optional(),
  myDataQrCode: z.string().optional(),
});

export const salesPersonSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  companySite: z.object({ id: z.string(), companyID: z.string(), name: z.string() }).optional(),
  userId: z.string().optional(),
  mwSalesPersonType: z
    .object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
      isActive: z.boolean(),
      isFromManager: z.boolean(),
      boid: z.string().optional(),
    })
    .optional(),
  sequence: z.number().optional(),
  rootCode: z.string().optional(),
  isActive: z.boolean().optional(),
  isFromManager: z.boolean().optional(),
  boid: z.string().optional(),
  onlineSearchLevel: z.union([z.literal(1), z.literal(2)]).optional(),
  salesPersonParentId: z.string().optional(),
  mwSalesPersonsAccessMaxLevel: z
    .object({
      id: z.string(),
      name: z.string(),
      level: z.number(),
    })
    .optional(),
  companySiteId: z.string().optional(),
  mwSalesPersonTypeId: z.string().optional(),
  mwSalesPersonsAccessMaxLevelId: z.string().optional(),
  user: z.object({ id: z.string(), userName: z.string() }).optional(),
});

export const getSalesDocumentsWithConditionsSchema = z.array(
  z.object({
    id: z.string(),
    createdDate: z.string(),
    officialDate: z.string().optional(),
    documentTypeName: z.string(),
    documentSeriesName: z.string(),
    documentStatusName: z.string(),
    documentNumber: z.number(),
    traderName: z.string().optional(),
    salesPersonName: z.string().optional(),
    totalGross: z.number(),
    totalCharges: z.number(),
    totalDiscounts: z.number(),
    isActive: z.boolean(),
    isFromManager: z.boolean(),
    deleted: z.boolean(),
  })
);

export type GetSalesDocumentsWithConditionsResponse = z.infer<
  typeof getSalesDocumentsWithConditionsSchema
>;
